// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from 'react-loading';

import Modal from '../Modal';
import Checkbox from '../../Checkbox';
import api from '../../../api';
import axios from 'axios';

import uploadFormats from '../../../data/formats';
import formatNumber from '../../../utilities/formatNumber';
import formatCurrency from '../../../utilities/formatCurrency';
import calculateBulkCost from '../../../utilities/calculateBulkCost';

import { PKCE_CODE_CHALLENGE, MS_GRAPH_CLIENT_ID } from '../../../data/keys';
import '../../../styles/import.css';

class ImportSharePoint extends Modal {

    state = {
        stage: 0,
        siteUrl: "",
        site: null,
        searching: false,
        oauth: null, // ID for authenticated OAuth object
        loading: true,
        alert: false,
        drives: [], // Drives - they have folders/driveItems in them
        stacks: [],
        avgNPages: 5,
        estimate: -1
    }

    nRequests = 0
    requestTimeouts = []
    cancelToken = axios.CancelToken.source() // Share across modal for easy unmounting

    componentDidMount = async () => {
        this._isMounted = true;
        const res = await api.get('/stack/all?minimal=true');
        if (res && res.success && res.stacks)
            this.setState({ stacks: res.stacks });
        else return toast.error("Failed to load stacks list");

        this.waitForTeamLoad = setInterval(() => {
            if (!this.props.team || !this.props.team.sync)
                return; // Try again...

            // Clear interval
            clearInterval(this.waitForTeamLoad);
            this.authenticateAndLoadRoot();
        }, 1000);
    }
    componentWillUnmount = ()=> {
        // Get the url params. 
        this.stopRecursiveCalls()
        const params = new URLSearchParams(window.location.search); 
        // Do we have a state? 
        let state = params.get('state'); 
        if(!state)
            return;

        state = JSON.parse(state);
        const redirect = state.redirect??null;
        if(redirect)
            window.close(); 
    }

    stopRecursiveCalls = () => {
        this._isMounted = false;
        this.cancelToken.cancel("Unmount");
        for (const timeout of this.requestTimeouts)
            if (timeout)
                clearTimeout(timeout);
    }

    authenticateAndLoadRoot = async () => {
        await this.setState({ loading: true });
        await this.getOAuth2Token();
    }

    getOAuth2Token = async () => {
        // Do we already have a token?
        const { code, connectionIdx } = this.props.data;
        if (!code && connectionIdx >= 0) {
            const connection = this.props.team.sync.sharepoint[connectionIdx];
            this.setState({
                site: connection.site,
                stage: 1
            });
            const res = await api.get("/sync/onedrive/refresh?oauth=" + connection.oauth);
            if (res && res.success) {
                this.setState({
                    stage: 1,
                    loading: true,
                    oauth: connection.oauth
                });
                setTimeout(async () => {
                    this.setState({
                        drives: await this.listDrives(),
                        loading: false
                    });
                },0);
                return;
            }

            // Re-authenticate...
            window.location.href = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" + MS_GRAPH_CLIENT_ID + "&response_type=code&response_mode=query&scope=offline_access%20files.read%20sites.read.all&code_challenge_method=S256&code_challenge=" + PKCE_CODE_CHALLENGE + "&redirect_uri=" + encodeURIComponent(api.publicUrl + "/settings/import/sharepoint");
            return;
        }

        // Code provided, or not reconnecting... Generate tokens with code
        const res = await api.post("/sync/onedrive/token", { code, sharepoint: true });
        if (!res || !res.oauthId)
            return toast.error("Unable to get a secure token from the Microsoft Graph API");

        await this.setState({ oauth: res.oauthId });
    }

    lookUpSiteURL = (siteUrl) => {
        if (this.searchTimeout)
            clearTimeout(this.searchTimeout);

        // Searching icon...
        this.setState({ siteUrl, searching: true, site: null });
        
        // Search after timeout
        this.searchTimeout = setTimeout(async () => {
            const res = await api.get("/sync/onedrive/sharepoint?oauth=" + this.state.oauth + "&siteUrl=" + siteUrl, {}, false, {}, true);
            this.setState({
                searching: false,
                site: res && res.site ? res.site : null
            });
        }, 500);
    }

    getSyncedFolders = () => {
        const team = this.props.team;
        const { connectionIdx } = this.props.data;
        return connectionIdx >= 0 && team && team.sync && team.sync.sharepoint && team.sync.sharepoint && team.sync.sharepoint.length > connectionIdx ? team.sync.sharepoint[connectionIdx].folders : [];
    }

    isSynced = (synced, drive, path, id) => {
        if (!synced || !synced.length)
            return false;

        // Already synced? (It is if a parent folder is synced)
        let sync = synced.map(f => f.id).includes(id);
        if (sync) return true;

        for (const f of synced) {
            if (f.drive === drive && path.substring(0, f.source.length) === f.source) {
                // f is a parent of entry, and f is synced - so this is automatically synced...
                return true;
            }
        }

        return false;
    }

    listDrives = async () => {
        const res = await api.get('/sync/onedrive/drives?oauth=' + this.state.oauth + '&siteId=' + this.state.site.id);
        if (!res || !res.drives || !res.drives.length) return [];
        return res.drives.map(d => ({
            ...d,
            folders: {},
            expanded: [],
            opened: false
        }));
    }

    updateDriveLocal = (idx, diff) => this.setState(prevState => {
        const drives = [ ...prevState.drives ];
        drives[idx] = {
            ...drives[idx],
            ...diff
        };
        return ({ drives });
    })

    getChildren = async (driveIdx, parent="", parentId="", name="") => {
        if (parent === "") // Loading icon...
            this.updateDriveLocal(driveIdx, { loading: true });
        else this.setState(prevState => {
            const drives = [ ...prevState.drives ];
            drives[driveIdx] = {
                ...drives[driveIdx],
                folders: {
                    ...drives[driveIdx].folders,
                    [parent]: {
                        ...drives[driveIdx].folders[parent],
                        id: parentId,
                        path: parent,
                        name,
                        sync: this.isSynced(synced, drive.id, parent, parentId),
                        loading: true
                    }
                },
                loading: false
            };
            return ({ drives });
        });

        const synced = this.getSyncedFolders();
        const drive = this.state.drives[driveIdx];

        const res = await api.get('/sync/onedrive/driveItems?oauth=' + this.state.oauth + '&drive=' + drive.id + (parentId.length ? '&parent=' + parentId : ''), {}, false, { cancelToken: this.cancelToken.token });
        if (!res || !res.driveItems)
            return toast.error("Unable to list folders in drive. Try again or contact your account manager.");

        for (const item of res.driveItems) {
            if (!item.folder) continue;

            const path = parent + "/" + item.name;
            // Scan folder synchronously
            this.setState(prevState => {
                const drives = [ ...prevState.drives ];
                drives[driveIdx] = {
                    ...drives[driveIdx],
                    folders: {
                        ...drives[driveIdx].folders,
                        [path]: {
                            ...drives[driveIdx].folders[path],
                            id: item.id,
                            path,
                            name: item.name,
                            nFolders: item.folder.childCount,
                            sync: this.isSynced(synced, drive.id, path, item.id)
                        }
                    },
                    loading: false
                };
                return ({ drives });
            });
        }

        if (parent === "") return;
        this.setState(prevState => {
            const drives = [ ...prevState.drives ];
            drives[driveIdx] = {
                ...drives[driveIdx],
                folders: {
                    ...drives[driveIdx].folders,
                    [parent]: {
                        ...drives[driveIdx].folders[parent],
                        loading: false
                    }
                }
            };
            return ({ drives });
        });
    }

    estimateImportCost = async () => {
        this.setState({ estimate: -2 }); // Loading...
        const folders = await this.getParentDirectoriesToSync(false); // Don't create new stacks yet

        // Recursively count files in each selected folder
        let estimate = 0;
        for (const f of folders)
            estimate += await this.countContainedFiles(f.drive, f.id)

        this.setState({ estimate });
    }

    countContainedFiles = async (driveId, parentId) => {
        if (!this._isMounted)
            return 0;

        const res = await api.get('/sync/onedrive/driveItems?oauth=' + this.state.oauth + '&drive=' + driveId + '&parent=' + parentId, false, {
            cancelToken: this.cancelToken.token
        });

        if (!res || !res.driveItems)
            return 0;

        let nDocs = 0; // Count number of files...
        for (const item of res.driveItems) {
            // This is a folder... Let's scan recursively
            if (item.folder)
                nDocs += await this.countContainedFiles(driveId, item.id);

            // If Stax·ai can accept this file, add to nDocs
            else if (item.file && uploadFormats.includes(item.name.split(".").pop().toLowerCase()))
                nDocs += 1;
        }

        return nDocs;
    }

    atleastOneFolderSelected = () => {
        for (const drive of this.state.drives)
            for (const f of Object.values(drive.folders))
                if (f.sync) return true;
        return false;
    }

    getParentDirectoriesToSync = async (createStack=true) => {
        // Compile folders to import
        let synced = [];
        for (const drive of this.state.drives)
            for (const f of Object.values(drive.folders))
                if (f.sync)
                    synced.push({
                        ...f,
                        drive: drive.id
                    });

        let folders = [];
        for (const f of synced) {
            // If a parent directory is being synced, then ignore
            let parentSynced = false;
            for (const g of synced) {
                if (f.drive !== g.drive) continue; // Don't check against folders in another drive
                if (f.id === g.id) continue; // Don't check against itself
                if (f.path.substring(0,g.path.length) === g.path) {
                    // g is a parent of f. Don't sync f.
                    parentSynced = true;
                    break;
                }
            }

            if (parentSynced) continue; // Skip

            // If the folder exists in Stax·ai, use that. Else, create folder in Stax·ai
            let destination;
            // let path = f.path_display;
            // if (path[0] === '/')
            //     path = path.substring(1);
            let path = f.name;

            const stack = this.state.stacks.find(s => s.path === path);
            if (stack)
                destination = stack._id;

            // Stack doesn't exist - create it
            else if (createStack) {
                const res = await api.post('/stack/create', { stackPath: path });
                if (!res || !res.stack) {
                    toast.error("Failed to create stack: " + path);
                    return this.props.loaded();
                }
                destination = res.stack._id;
            }

            // We're good - sync this folder...
            folders.push({
                source: f.path,
                name: f.name,
                id: f.id,
                drive: f.drive,
                destination
            });
        }
        return folders;
    }

    renderDrives = () => {
        // Drives are top-level
        return (<>
            {this.state.drives.map((d,idx) => {
                const name = d.name ? d.name : d.driveType === "personal" ? "My Personal Drive" : d.driveType === "business" ? "My Business Drive" : "Unnamed Drive (" + d.driveType + ")";
                return <div key={idx}>
                    <div
                        className="folder"
                        style={{ paddingLeft: "8px" }}
                        title={name}
                    >
                        {/* Dropdown arrow */}
                        <div
                            className="config-stack-dropdown-arrow folder-select-dropdown-control"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                const drives = [ ...this.state.drives ];
                                // Load drive contents if now opened...
                                if (!d.opened && !d.folders.length) {
                                    drives[idx].opened = true;
                                    drives[idx].loading = true;

                                    // Load drive folders synchronously...
                                    this.getChildren(idx);
                                }
                                else drives[idx].opened = false;
                                this.setState({ drives });
                            }}
                        >
                            {d.opened ? "▾" : "▸"}
                        </div>

                        <img
                            src={"https://static.stax.ai/images/icons/windows-drive.png"}
                            alt=""
                        />
                        <div className="folder-name">{name}</div>
                    </div>

                    {/* Drive folders here... */}
                    {d.opened ? d.loading ?
                    
                        // Loading...
                        <div
                            className="folder"
                            style={{ paddingLeft: "40px" }}
                            title="Loading..."
                        >
                            <div className="config-stack-no-dropdown folder-select-dropdown-control">▸</div>
                            <div className="folder-loading-icon">
                                <Loading
                                    type="bubbles"
                                    color="var(--color-primary)"
                                    height={28}
                                    width={28}
                                />
                            </div>
                            <div className="folder-name folder-loading-text">Loading...</div>
                        </div> : 
                    
                        // Render folders
                        this.renderFolders(idx)

                    : ""}
                </div>
            })}
        </>);
    }

    renderFolders = (driveIdx, parent="", indent=0) => {
        const drive = this.state.drives[driveIdx];

        let folders = [];
        for (const [path, f] of Object.entries(drive.folders)) {
            // Not the parent directory!
            if (path === parent)
                continue;

            // Only folders inside the given parent directory
            if (path.substring(0, parent.length) !== parent)
                continue;

            // Only one level down...
            const subpath = path.substring(parent.length+1);
            const cmp = subpath.split("/");

            if (cmp.length > 1)
                continue;

            folders.push(f); // We can display this folder...
        }

        return (<>
            {folders.map((f,idx) => {
                // Are there any folders inside?
                return <div key={idx}>
                    <div
                        className="folder"
                        style={{ paddingLeft: String(40 + (indent*32)) + "px" }}
                        title={f.name}
                    >
                        {/* Dropdown arrow */}
                        {f.nFolders ? <div
                            className="config-stack-dropdown-arrow folder-select-dropdown-control"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                // Load nested folders if not already loaded
                                if (!f.childrenLoaded && !drive.expanded[f.id])
                                    this.getChildren(driveIdx, f.path, f.id, f.name);

                                this.updateDriveLocal(driveIdx, {
                                    expanded: {
                                        ...drive.expanded,
                                        [f.id]: drive.expanded[f.id] ? false : true
                                    }
                                });
                            }}
                        >
                            {drive.expanded[f.id] ? "▾" : "▸"}
                        </div> : <div className="config-stack-no-dropdown folder-select-dropdown-control">▸</div>}

                        <img
                            src="https://static.stax.ai/images/icons/folder.png"
                            alt=""
                        />
                        <div className="folder-name">{f.name}</div>

                        {/* Still loading? */}
                        {f.loading ? <div className="folder-loading">
                            <Loading
                                type="spin"
                                color="var(--color-accent)"
                                height={20}
                                width={20}
                            />
                        </div> : ""}

                        <Checkbox
                            className="import-checkbox"
                            tooltip="Import and sync folder to Stax·ai. Right-click for options."
                            checked={f.sync}
                            onToggle={() => this.setState(prevState => {
                                const sync = !f.sync;
                                const drives = [ ...prevState.drives ];
                                let folders = { ...drive.folders };

                                // Nested operations...
                                for (const path of Object.keys(folders)) {
                                    // Apply to this and all sub-folders inside this one
                                    if (path.substring(0, f.path.length) === f.path)
                                        folders[path].sync = sync;

                                    // If we're unchecking a folder sync, then make sure we also uncheck all parents up to root
                                    if (!sync && f.path.substring(0, path.length) === path)
                                        folders[path].sync = false;
                                };

                                drives[driveIdx].folders = folders;
                                return ({ drives, estimate: -1 });
                            })}
                        />
                    </div>

                    {/* Nested folders */}
                    {drive.expanded[f.id] ? this.renderFolders(driveIdx, f.path, indent+1) : ""}
                </div>;
            })}
        </>);
    }

    renderContent = () => {
        return (<div className="modal-content modal-center">

            <h1>Sync documents from SharePoint site.</h1>

            <p>
                Stax·ai will make stacks for each folder on the SharePoint site you select below and mirror the contents of these folders into your new stacks.
            </p><br />

            {this.state.stage === 0 ? <>
                <h2>Find SharePoint site</h2>
                <div className="import-sharepoint-url">
                    <input
                        id="siteurl"
                        name="siteurl"
                        type="text"
                        placeholder="Enter sharepoint site URL here..."
                        style={{width: 400 }}
                        value={this.state.siteUrl}
                        onClick={e => e.stopPropagation()}
                        onChange={e => this.lookUpSiteURL(e.target.value)}
                    />
                    {this.state.searching ? <div className="import-sharepoint-url-searching">
                        <Loading
                            type="spin"
                            color="var(--color-primary)"
                            height={28}
                            width={28}
                        />
                    </div> : ""}
                </div>

                {this.state.site && this.state.site.id ? <>
                    <div className="import-sharepoint-site">
                        <h1>{this.state.site.name}</h1>
                        <h2>{this.state.site.host}</h2>
                        <div
                            className="import-sharepoint-site-open"
                            onClick={() => window.open(this.state.site.url, "_blank")}
                        />
                    </div>

                    <div className="import-control">
                        <div
                            className="button"
                            style={{
                                float: 'none',
                                backgroundImage: "url('https://static.stax.ai/images/icons/select-folders.png')"
                            }}
                            onClick={async () => {
                                // Do we have a state? 
                                let { connectionIdx } = this.props.data;
                                connectionIdx = connectionIdx??0;
                                const params = new URLSearchParams(window.location.search); 

                                let state = params.get('state'); 
                                if(state){
                                    // Enable Sharepoint Import
                                    const connections = this.props.team && this.props.team.sync && this.props.team.sync.sharepoint && this.props.team.sync.sharepoint.length ? this.props.team.sync.sharepoint : [];
                                    let connection;
                                    if (connectionIdx >= 0 && connections.length > connectionIdx)
                                        connection = connections[connectionIdx];
                                    else {
                                        // Connection doesn't exist... create one
                                        connection = {
                                            creationDate: new Date(),
                                            user: this.props.user._id
                                        }
                                    }

                                    // Common updates
                                    connection.enabled = true;
                                    connection.folders = [];
                                    connection.oauth = this.state.oauth;
                                    connection.bulk = false;
                                    connection.lastModified = new Date();
                                    connection.error = "";
                                    connection.moveUploadedFiles = this.state.moveUploadedFiles;
                                    connection.site = this.state.site; 

                                    if (connectionIdx >= 0 && connections.length > connectionIdx)
                                        connections[connectionIdx] = connection;
                                    else 
                                        connections.push(connection); // Create new
                        
                                    const sync = this.props.team ? this.props.team.sync : {};
                                    sync.sharepoint = connections;
                                    await this.props.updateTeam({ sync });
                        
                                    this.props.modal();

                                }

                                this.setState({ stage: 1, loading: true });
                                setTimeout(async () => {
                                    this.setState({
                                        drives: await this.listDrives(),
                                        loading: false
                                    });
                                },0);
                            }}
                        >Select folders to sync</div>
                    </div>
                </> : this.state.siteUrl.length && !this.state.searching ? <div className="import-sharepoint-site-notfound">
                    <h1>Could not find sharepoint site</h1>
                    <p>Paste the URL from the browser address bar that you use to access the sharepoint site in the textbox above.</p>
                </div> : ""}
            </> : <>
                <h2>Select SharePoint site folders to import</h2>
                <div className="folder-select">
                    {this.state.loading ? <div style={{
                        marginLeft: 300,
                        marginTop: 60
                    }}><Loading
                        type="bubbles"
                        color="var(--color-primary)"
                        height={100}
                        width={100}
                    /></div> : this.state.drives && this.state.drives.length > 0 ? this.renderDrives() : <div style={{ textAlign: 'center' }}>We couldn't find any drives...</div>}
                </div>
                {this.atleastOneFolderSelected(this.state.drives) && <div className="import-control">
                    <div
                        className="button"
                        style={{
                            float: 'none',
                            backgroundImage: "url('https://static.stax.ai/images/icons/import.png')"
                        }}
                        onClick={async () => {
                            const { connectionIdx } = this.props.data;
                            this.stopRecursiveCalls(); // Stop all existing calls - they might be going on for a while.

                            await this.props.loading(2000, "Setting up...");

                            // Compile folders to import
                            const folders = await this.getParentDirectoriesToSync();
                            
                            // Enable OneDrive Import
                            const connections = this.props.team && this.props.team.sync && this.props.team.sync.sharepoint && this.props.team.sync.sharepoint.length ? this.props.team.sync.sharepoint : [];

                            let connection;
                            if (connectionIdx >= 0 && connections.length > connectionIdx)
                                connection = connections[connectionIdx];
                            else {
                                // Connection doesn't exist... create one
                                connection = {
                                    creationDate: new Date(),
                                    site: this.state.site,
                                    user: this.props.user._id
                                }
                            }
                                
                            // Common updates
                            connection.enabled = true;
                            connection.folders = folders;
                            connection.oauth = this.state.oauth;
                            connection.bulk = true;
                            connection.lastModified = new Date();
                            connection.error = "";

                            if (connectionIdx >= 0 && connections.length > connectionIdx)
                                connections[connectionIdx] = connection;
                            else
                                connections.push(connection); // Create new

                            const sync = this.props.team ? this.props.team.sync : {};
                            sync.sharepoint = connections;
                            await this.props.updateTeam({ sync });

                            // Force a manual sync now
                            const res = await api.get('/sync/connections?source=sharepoint');
                            if (res && res.connections && res.connections.length)
                                await api.post('/sync/manual', {
                                    connectionId: res.connections[connectionIdx >= 0 ? connectionIdx : connections.length-1]._id,
                                    source: "sharepoint"
                                });

                            toast.info("Your OneDrive files will start syncing shortly");
                            this.props.loaded();
                            this.props.modal();
                        }}
                    >Import Selected Folders</div>
                    {this.state.estimate >= 0 ? <div className="import-cost">
                        You are requesting a <strong>bulk import</strong> of {formatNumber(this.state.estimate)} documents.<br />
                        With an estimated average of <input
                            className="input-inline"
                            type="text"
                            value={this.state.avgNPages}
                            onChange={e => {
                                const n = parseInt(e.target.value);
                                this.setState({ avgNPages: isNaN(n) ? 0 : n });
                            }}
                        /> pages per document,<br />
                        your bulk import charge will be approx. <strong>{formatCurrency(calculateBulkCost(this.state.avgNPages*this.state.estimate))}.</strong>
                    </div> : this.state.estimate < -1 ? <div className="import-estimate-calculating" >
                        <div className="import-estimate-calculating-icon">
                            <Loading
                                type="bubbles"
                                color="var(--color-primary)"
                                height={28}
                                width={28}
                            />
                        </div>
                        <div className="import-estimate-calculating-text">Estimating...</div>
                    </div> : <div
                        className="import-estimate-cost"
                        onClick={() => this.estimateImportCost()}
                    >Estimate import cost</div>}
                </div>}
            </>}
        </div>);
    }

};

export default withRouter(ImportSharePoint);